import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ArrowLeft, History, ShieldCheck, FileText } from 'lucide-react';

const UserHistoryPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const { t } = useLanguage();

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  if (!currentUser) return null;

  return (
    <div className="min-h-screen flex flex-col vault-bg">
      <header className="header-nav px-4 py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="hidden sm:inline">{t('back')}</span>
          </Button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="BMPay" className="h-7 w-7" />
            <span className="font-bold text-[#0A2540] font-['Manrope']">BMPay</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center p-4 sm:p-6">
        <div className="w-full max-w-md animate-slide-up">
          <Card className="secure-card-navy rounded-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-b from-slate-50 to-white px-5 pt-6 pb-4">
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full flex items-center justify-center bg-[#0A2540]/10">
                  <History className="h-7 w-7 text-[#0A2540]" />
                </div>
              </div>
              <h1 className="text-lg sm:text-xl font-bold text-[#0A2540] font-['Manrope'] text-center" data-testid="history-title">
                {t('transferHistory')}
              </h1>
              <p className="text-xs text-slate-600 mt-1 text-center">
                {t('viewTransferRequests')}
              </p>
            </CardHeader>

            <CardContent className="px-5 py-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-slate-600 text-sm mb-2">{t('noTransferInProgress')}</p>
                <p className="text-slate-500 text-xs">
                  {t('transferRequestsWillAppear')}
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 text-center">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 text-sm"
            >
              {t('backToDashboard')}
            </Button>
          </div>
        </div>
      </main>

      <footer className="py-3 text-center border-t border-slate-200 bg-white/50">
        <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
          <ShieldCheck className="h-3 w-3 text-emerald-600" />
          <span>{t('securedByBMPay')}</span>
        </div>
      </footer>
    </div>
  );
};

export default UserHistoryPage;
